import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import {
  IconButton,
  Stack,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import { getStickyActionsCellSx } from "../constants";

interface Props {
  onSortChange: (column: string, direction: "asc" | "desc") => void;
  sortColumn?: string;
  sortDirection?: "asc" | "desc";
  hasHorizontalOverflow: boolean;
}

interface Column {
  id: string;
  label: string;
  sortable: boolean;
  // Percentages so the flexible columns grow together. LINK and ACTIONS stay in
  // pixels: their contents are one icon and a fixed set of buttons, so extra
  // width would only pad them out.
  width: string;
  sticky?: boolean;
  tooltip?: string;
}

const columns: Column[] = [
  {
    id: "carrier",
    label: "CARRIER",
    sortable: false,
    width: "31%",
  },
  {
    id: "price",
    label: "PRICE",
    sortable: true,
    width: "13%",
  },
  {
    id: "equipment",
    label: "EQUIPMENT",
    sortable: false,
    width: "14%",
  },
  {
    id: "pickup",
    label: "PICKUP",
    sortable: true,
    width: "12%",
  },
  {
    id: "delivery",
    label: "DELIVERY",
    sortable: true,
    width: "12%",
  },
  {
    id: "link",
    label: "LINK",
    sortable: false,
    width: "56px",
    tooltip:
      "Every booking gets tracking updates on Evotrux. This column is only about whether the carrier offered a live tracking link on their quote.",
  },
  {
    id: "actions",
    label: "ACTIONS",
    sortable: false,
    width: "200px",
    sticky: true,
  },
];

export function ShipmentQuoteTableHeader(props: Props) {
  const { onSortChange, sortColumn, sortDirection, hasHorizontalOverflow } =
    props;

  const handleSortClick = (columnId: string) => {
    if (sortColumn === columnId) {
      // Toggle direction
      const newDirection = sortDirection === "asc" ? "desc" : "asc";
      onSortChange(columnId, newDirection);
    } else {
      // New column, default to ascending
      onSortChange(columnId, "asc");
    }
  };

  return (
    <TableHead>
      <TableRow>
        {columns.map((column) => (
          <TableCell
            key={column.id}
            sx={[
              {
                width: column.width,
                fontWeight: "bold",
                backgroundColor: "grey.50",
              },
              !!column.sticky &&
                getStickyActionsCellSx(hasHorizontalOverflow, 3),
            ]}
          >
            {column.sortable ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {column.label}
                </Typography>
                <Stack direction="column" spacing={0}>
                  <IconButton
                    size="small"
                    onClick={() => handleSortClick(column.id)}
                    sx={{
                      p: 0,
                      minHeight: 16,
                      "& .MuiSvgIcon-root": {
                        fontSize: 16,
                        color:
                          sortColumn === column.id && sortDirection === "asc"
                            ? "primary.main"
                            : "grey.400",
                      },
                    }}
                  >
                    <KeyboardArrowUp />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleSortClick(column.id)}
                    sx={{
                      p: 0,
                      minHeight: 16,
                      "& .MuiSvgIcon-root": {
                        fontSize: 16,
                        color:
                          sortColumn === column.id && sortDirection === "desc"
                            ? "primary.main"
                            : "grey.400",
                      },
                    }}
                  >
                    <KeyboardArrowDown />
                  </IconButton>
                </Stack>
              </Stack>
            ) : (
              <Tooltip title={column.tooltip ?? ""}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {column.label}
                </Typography>
              </Tooltip>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}
